import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { APP_LINKS } from "../constants/app-links";

/**
 * Sticky banner dính đáy nhắc tải App Longhousee.
 * - Chỉ hiện trên mobile web (md:hidden) — desktop dùng QR ở /tai-app.
 * - Ẩn trên chính trang /tai-app (tránh trùng lặp).
 * - Dismiss được: lưu mốc thời gian, tự hiện lại sau 48h để không làm phiền liên tục.
 */
const DISMISS_KEY = "app_install_banner_dismissed_at"; // lưu timestamp (ms) lúc dismiss
const RESHOW_AFTER_MS = 48 * 60 * 60 * 1000; // hiện lại sau 48 giờ

// Route ẩn banner: trang tải app + các luồng tập trung (auth) tránh che form.
// /flash-sale mở từ ô tiện ích TRONG app — mời tải app ở đó là thừa, lại che mất hàng
// card cuối cùng.
const HIDDEN_PATHS = [APP_LINKS.internalPath, "/login", "/signup", "/forgot-password", "/flash-sale"];
// Prefix ẩn banner: landing page /trang/:slug hiển thị full-page (nhúng cả trong
// WebView app) nên không chèn thêm gì đè lên.
const HIDDEN_PREFIXES = ["/trang/"];

function AppInstallBanner() {
    const { pathname } = useLocation();
    const [dismissed, setDismissed] = useState(true); // mặc định ẩn tới khi check xong (tránh nháy)

    useEffect(() => {
        try {
            const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
            // Còn trong cửa sổ 48h kể từ lần dismiss → tiếp tục ẩn; ngược lại hiện lại.
            const stillHidden = dismissedAt > 0 && Date.now() - dismissedAt < RESHOW_AFTER_MS;
            setDismissed(stillHidden);
        } catch {
            setDismissed(false); // storage bị chặn (chế độ riêng tư) → vẫn cho hiện banner
        }
    }, []);

    const handleDismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            // bỏ qua nếu storage bị chặn — vẫn ẩn trong phiên hiện tại
        }
        setDismissed(true);
    };

    // Không hiện khi đã dismiss hoặc ở các route bị loại trừ.
    if (dismissed || HIDDEN_PATHS.includes(pathname)) return null;
    if (HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return null;

    return (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-surface border-t border-line shadow-[0_-2px_10px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center gap-3 px-3 py-2.5">
                <img
                    src="/img/ui/longicon.png"
                    alt="Longhousee"
                    className="w-9 h-9 rounded-xl shrink-0"
                />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-content leading-tight">
                        Tải App Longhousee
                    </p>
                    <p className="text-[11px] text-content-3 leading-tight">
                        Mua qua App nhận x2 hoàn tiền
                    </p>
                </div>
                <Link
                    to={APP_LINKS.internalPath}
                    aria-label="Tải App Longhousee"
                    className="shrink-0 bg-gradient-to-r from-brand-red to-brand-warm text-white text-xs font-bold rounded-full px-4 py-2 active:scale-95 transition-all"
                >
                    Tải
                </Link>
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label="Đóng"
                    className="shrink-0 w-7 h-7 flex items-center justify-center text-content-3 active:scale-90"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>
        </div>
    );
}

export default AppInstallBanner;
